import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

const SCRIPT_SRC_BASE = 'https://app.termly.io';

/**
 * Termly Consent Management Platform (CMP) Component
 *
 * Dynamically loads the Termly cookie consent banner and manages script lifecycle.
 * The banner will appear on all pages and handle user consent preferences.
 *
 * @param {boolean} autoBlock - Enable automatic blocking of scripts until consent is granted
 * @param {string} masterConsentsOrigin - Optional origin for master consents
 * @param {string} websiteUUID - Your Termly website UUID from the dashboard
 *
 * IMPORTANT: Required Termly Dashboard Configuration
 * ================================================
 * For profile pictures to display properly before cookie consent, the following domains
 * must be added to the "Essential" category in the Termly dashboard:
 *
 * Gravatar (team member photos on About page):
 *    - 0.gravatar.com
 *    - 1.gravatar.com
 *    - 2.gravatar.com
 *    - 3.gravatar.com
 *    - api.gravatar.com
 *
 * AWS S3 (user profile pictures):
 *    - nsf-sustainability-hub.s3.us-east-1.amazonaws.com (production)
 *    - nsf-sustainability-hub-dev.s3.us-east-1.amazonaws.com (development)
 *
 * Why: These images are essential functionality, not tracking/analytics.
 * Without this configuration, images will be blocked until users accept cookies.
 */
export default function TermlyCMP({
    autoBlock = true,
    masterConsentsOrigin,
    websiteUUID,
}) {
    // Build the script URL with query parameters
    const scriptSrc = useMemo(() => {
        const src = new URL(SCRIPT_SRC_BASE);
        src.pathname = `/resource-blocker/${websiteUUID}`;

        if (autoBlock) {
            src.searchParams.set('autoBlock', 'on');
        }

        if (masterConsentsOrigin) {
            src.searchParams.set('masterConsentsOrigin', masterConsentsOrigin);
        }

        return src.toString();
    }, [autoBlock, masterConsentsOrigin, websiteUUID]);

    const isScriptAdded = useRef(false);

    // Add the Termly script to the document head (only once)
    useEffect(() => {
        if (isScriptAdded.current) return;

        const script = document.createElement('script');
        script.src = scriptSrc;
        script.async = true;
        document.head.appendChild(script);

        isScriptAdded.current = true;

        // Cleanup function to remove script if component unmounts
        return () => {
            const existingScript = document.querySelector(
                `script[src="${scriptSrc}"]`
            );
            if (existingScript) {
                existingScript.remove();
            }
        };
    }, [scriptSrc]);

    // Re-initialize Termly when route changes (for SPA navigation)
    const location = useLocation();

    useEffect(() => {
        // Wait for Termly to be available, then initialize
        if (window.Termly && typeof window.Termly.initialize === 'function') {
            window.Termly.initialize();
        }
    }, [location.pathname, location.search]);

    // This component doesn't render any visible elements
    return null;
}

TermlyCMP.propTypes = {
    autoBlock: PropTypes.bool,
    masterConsentsOrigin: PropTypes.string,
    websiteUUID: PropTypes.string.isRequired,
};
